import { HttpService, createHttpService } from '@/services/HttpService';
import { MeResponse } from '@/services/UserService';

export type TenantLicensesResponse = {
  subscriptions: TenantLicense[],
};

type TenantUsageItem = {
  absolute_value: number
};

type TenantStorageUsageResponse = {
  items: TenantUsageItem[]
};

export type TenantLicense = {
  uuid: string;
  tenant_uuid: string;
  is_auto_renewal_enabled: boolean;
  title: string;
  bundle_id: string;
};

export type DeleteAccountReason = {
  reason: string,
  comment: string
};

export type RequestAccountDeletionPayload = DeleteAccountReason & {user_id: string};

export type DeleteAccountConfirmationPayload = {token: string, tenantId: string};

type RequestAccountDeletionResponse = {};

export enum DeleteAccountConfirmationStatus {
  success = 'success',
  expired = 'expired',
  error = 'error',
}

export enum CustomerType {
  atih = 'atih',
  acronis_backup = 'acronis_backup',
}

export type ConfirmAccountDeletionResponse = {
  data: {
    status: DeleteAccountConfirmationStatus.success | DeleteAccountConfirmationStatus.expired,
  }
};

export type GetCustomerTypeResponse = {
    customer_type: CustomerType
};


export class DeleteAccountService {
  constructor(
    private tenantUuid: string,
    private httpService: HttpService
  ) {}

  public async getTenantLicenses(): Promise<TenantLicensesResponse> {
    try {
      return await this.httpService.get<void, TenantLicensesResponse>(
          `/api/subscriptions-manager/v1/tenants/${this.tenantUuid}/subscriptions/names?active=true`
        );
    } catch (error) {
      return Promise.resolve({
        subscriptions: []
      });
    }
  }

  public async getTenantStorageUsage(): Promise<number> {
    try {
      const tenantStorageUsageResponse = await this.httpService.get<void, TenantStorageUsageResponse>(
          `/api/2/tenants/${this.tenantUuid}/usages?usage_names=storage`
        );

      return this.sumTenantUsage(tenantStorageUsageResponse.items);
    } catch (error) {
      return 0;
    }
  }

  public async requestAccountDeletion(payload: DeleteAccountReason): Promise<void> {
    try {
      const meResponse = await this.httpService.get<void, MeResponse>('/api/2/users/me');
      await this.httpService.post<RequestAccountDeletionPayload, RequestAccountDeletionResponse>(
        `/api/self-management/v1/accounts/${this.tenantUuid}/delete`,
        { ...payload, user_id: meResponse.id }
      );
    } catch (error) {}

    return Promise.resolve();
  }

  public async confirmAccountDeletion(payload: DeleteAccountConfirmationPayload):
    Promise<keyof typeof DeleteAccountConfirmationStatus> {
    try {
      const response = await this.httpService.delete<void, ConfirmAccountDeletionResponse>(
        `/api/self-management/v1/accounts/${payload.tenantId}?token=${payload.token}`
      );

      return response.data.status;
    } catch (error) {
      return DeleteAccountConfirmationStatus.error;
    }
  }

  public async getCustomerType(): Promise<CustomerType | void> {
    try {
      const response = await this.httpService.get<void, GetCustomerTypeResponse>(
        `/api/2/tenants/${this.tenantUuid}`
      );

      return response.customer_type;
    } catch (error) {
    }
  }

  private sumTenantUsage(items: TenantUsageItem[]) {
    return items.reduce((acc, item) => (acc + item.absolute_value), 0);
  }
}

export function createDeleteAccountService(selfTenantUuid: string, tenantUuid: string): DeleteAccountService {
  const httpService: HttpService = createHttpService(selfTenantUuid);

  return new DeleteAccountService(tenantUuid, httpService);
}
